/**
 * Identity, device, permission and session models for משימות שינוע 99.
 *
 * These types are the single source of truth for the authentication layer:
 * the permissions engine (`src/core/permissions.ts`), the offline store
 * (`src/db/auth-store.ts`) and the auth UI (`src/modules/auth/`) all build
 * on the shapes declared here.
 */
import type { ISODateString } from './index';

/** Role hierarchy. `super_admin` is unrestricted, `soldier` is the narrowest. */
export type UserRole = 'super_admin' | 'admin' | 'soldier';

/** Lifecycle state of an account, independent of device approval. */
export type UserStatus = 'active' | 'pending' | 'suspended';

/**
 * Every granular action the system can guard.
 * The literal union `UserPermission` is derived from this list, so adding an
 * action here is enough to make it part of the type system.
 */
export const USER_PERMISSIONS = [
  'VIEW_TASKS',
  'VIEW_ALL_TASKS',
  'CREATE_TASK',
  'EDIT_TASK',
  'DELETE_TASK',
  'ASSIGN_TASK',
  'OVERRIDE_ASSIGNMENT',
  'UPDATE_TASK_STATUS',
  'MANAGE_VEHICLES',
  'MANAGE_SOLDIERS',
  'MANAGE_USERS',
  'RESET_USER_PIN',
  'APPROVE_DEVICE',
  'DISCONNECT_DEVICE',
  'MANAGE_SYSTEM_SETTINGS',
  'VIEW_REPORTS',
  'VIEW_AUDIT_LOGS',
  'EXPORT_DATA',
] as const;

export type UserPermission = (typeof USER_PERMISSIONS)[number];

/**
 * Per-admin configuration flags.
 * `true` grants an action beyond the admin baseline, `false` revokes one from
 * it. Missing keys fall back to the role baseline.
 */
export type PermissionOverrides = Partial<Record<UserPermission, boolean>>;

/**
 * Per-account display choices, set from "עריכת פרטים אישיים". They never widen
 * or narrow a permission — they only decide what the operator's own chrome
 * shows them.
 */
export interface UserPreferences {
  /**
   * Whether the role badge ("מנהל על") rides along in the application header.
   * The badge is the super-admin's own indicator, so it is only ever offered
   * to that account — see `canSeeRoleBadge()` in `src/core/permissions.ts`.
   */
  showRoleBadge: boolean;
  /** Show the company / platoon line under the name in the header. */
  showUnitLine: boolean;
}

export const DEFAULT_USER_PREFERENCES: UserPreferences = {
  showRoleBadge: true,
  showUnitLine: false,
};

export interface User {
  id: string;
  /** Normalised Israeli mobile number, digits only (e.g. `0500000001`). */
  phone: string;
  fullName: string;
  /** Military personal number. */
  personalNumber: string;
  role: UserRole;
  company: string;
  platoon: string;
  status: UserStatus;
  /** A super-admin has vetted this account. */
  isApproved: boolean;
  createdAt: ISODateString;
  /** Admin-only: custom permission flags on top of the role baseline. */
  permissionOverrides?: PermissionOverrides;
  /** Display choices the account set for itself. */
  preferences?: UserPreferences;
  /**
   * Set the first time the operator edits their own details. It is what stops
   * the seed from writing the shipped name and phone back over their choice on
   * the next boot — see `seedMockUsers()` in `src/db/auth-store.ts`.
   */
  profileUpdatedAt?: ISODateString;
  /**
   * The account's id in the central D1 directory, when it has one.
   *
   * The local id is never rewritten to match it — the seeded owner's id is
   * named by the safety guards in `src/db/auth-store.ts` — so this is the only
   * way back to the row the cloud holds. Absent on an account that was opened
   * locally and never signed in against the directory (a נהג's, typically).
   */
  cloudId?: string;
  /**
   * The role the directory holds for the account (`admin` / `manager`).
   *
   * A *reading*, never a second source of truth: `role` above is what
   * `hasPermission()` enforces, and this records which directory level it was
   * derived from. See `src/core/cloud-auth.ts`.
   */
  cloudRole?: 'admin' | 'manager';
}

/**
 * A physical device bound to a user. Sign-in is only completed once the
 * device fingerprint itself has been approved by a super-admin.
 */
export interface Device {
  id: string;
  userId: string;
  /** SHA-256 digest of stable browser/hardware signals. */
  deviceFingerprint: string;
  /** Human readable label, e.g. `Chrome · Windows`. */
  deviceName: string;
  isApproved: boolean;
  lastActive: ISODateString;
  approvedAt: ISODateString | null;
  /** Id of the super-admin that approved the device. */
  approvedBy?: string;
  createdAt: ISODateString;
}

/**
 * How the current session was last unlocked.
 *
 * `password` is the centralised sign-in — phone plus the group's shared code,
 * checked against the D1 directory. `otp` is the local simulated challenge it
 * falls back to with no signal, and `pin` is the 4-digit code a cold launch
 * asks for. The label is recorded, never trusted: what a session may do comes
 * from the account, not from how it was opened.
 */
export type UnlockMethod = 'otp' | 'pin' | 'password';

/** The active session, persisted locally so the app works offline. */
export interface AuthSession {
  /** Always `current` — a single active session per installation. */
  id: string;
  userId: string;
  deviceId: string;
  deviceFingerprint: string;
  role: UserRole;
  issuedAt: ISODateString;
  expiresAt: ISODateString;
  lastSeenAt: ISODateString;
  unlockMethod: UnlockMethod;
}

/**
 * Locally stored 4-digit PIN, kept as a salted PBKDF2 digest.
 * The PIN itself is never persisted and cannot be recovered from this record.
 */
export interface LocalPinRecord {
  userId: string;
  algorithm: 'PBKDF2-SHA256';
  iterations: number;
  /** Base64 random salt. */
  salt: string;
  /** Base64 derived key. */
  hash: string;
  updatedAt: ISODateString;
}

/** Minimal task shape the permission engine needs for ownership checks. */
export interface TaskScope {
  assignedUserIds: readonly string[];
}

/** A one-time-password challenge (simulated — no SMS gateway yet). */
export interface OtpChallenge {
  id: string;
  phone: string;
  code: string;
  createdAt: ISODateString;
  expiresAt: ISODateString;
  attemptsLeft: number;
}
